import { WebNextKnownEventName } from "./eventCatalog";
import {
  logFirebaseAnalyticsEvent,
  setFirebaseAnalyticsUserId,
} from "./firebaseAdapter";
import {
  validateAnalyticsEventName,
  validateAnalyticsParamKey,
} from "./eventValidation";

export type AnalyticsParams = Record<string, unknown>;

type ServerQueuedAnalyticsEvent = {
  name: string;
  properties?: AnalyticsParams;
};

const MAX_STRING_LENGTH = 500;

let hasFlushedPendingServerEvents = false;

const COOKIE_ESCAPE_RE = /[.*+?^${}()|[\]\\]/g;
const cookieRegexCache = new Map<string, RegExp>();

function warnInDev(message: string, ...context: unknown[]): void {
  if (!import.meta.env.PROD) {
    console.warn(`[analytics] ${message}`, ...context);
  }
}

function getCookieRegex(name: string): RegExp {
  let re = cookieRegexCache.get(name);
  if (!re) {
    const escapedName = name.replace(COOKIE_ESCAPE_RE, "\\$&");
    re = new RegExp(`(?:^|; )${escapedName}=([^;]*)`);
    cookieRegexCache.set(name, re);
  }
  return re;
}

function readCookieValue(name: string): string | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }

  const match = document.cookie.match(getCookieRegex(name));
  if (!match?.[1]) {
    return undefined;
  }

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function readRuntimeSessionId(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  const runtime = window as unknown as Record<string, unknown>;
  const directSessionHash = runtime.session_hash;
  if (typeof directSessionHash === "string" && directSessionHash.trim().length > 0) {
    return directSessionHash;
  }

  const gcConfig = window.gc?.global?.config as
    | Record<string, unknown>
    | undefined;
  const sessionFromConfig = gcConfig?.session_id;
  if (typeof sessionFromConfig === "string" && sessionFromConfig.trim().length > 0) {
    return sessionFromConfig;
  }

  return undefined;
}

let cachedCommonParams: AnalyticsParams | null = null;
let cachedCommonParamsAt = 0;
const COMMON_PARAMS_TTL_MS = 5000;

function readCommonAnalyticsParams(): AnalyticsParams {
  const now = Date.now();
  if (cachedCommonParams && now - cachedCommonParamsAt < COMMON_PARAMS_TTL_MS) {
    return cachedCommonParams;
  }
  cachedCommonParams = sanitizeAnalyticsParams({
    guest_id: readCookieValue("gcg2_id"),
    session_id: readRuntimeSessionId(),
  });
  cachedCommonParamsAt = now;
  return cachedCommonParams;
}

function normalizeAnalyticsParamKey(rawKey: string): string {
  return rawKey
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .toLowerCase();
}

function sanitizeAnalyticsParamValue(value: unknown, depth = 0): unknown {
  if (value === undefined) {
    return undefined;
  }

  if (value === null) {
    return null;
  }

  if (typeof value === "string") {
    return value.length > MAX_STRING_LENGTH ? value.slice(0, MAX_STRING_LENGTH) : value;
  }

  if (typeof value === "number") {
    return value;
  }

  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeAnalyticsParamValue(item, depth + 1))
      .filter((item) => item !== undefined);
  }

  if (typeof value === "object") {
    if (depth > 4) {
      return JSON.stringify(value).slice(0, MAX_STRING_LENGTH);
    }

    const sanitized: Record<string, unknown> = {};
    for (const [key, nestedValue] of Object.entries(value as Record<string, unknown>)) {
      const normalizedKey = normalizeAnalyticsParamKey(key);
      const keyValidation = validateAnalyticsParamKey(normalizedKey);
      if (!keyValidation.valid) {
        warnInDev("Dropping analytics object key with invalid name.", key, keyValidation.reason);
        continue;
      }

      const normalizedValue = sanitizeAnalyticsParamValue(nestedValue, depth + 1);
      if (normalizedValue === undefined) {
        continue;
      }

      sanitized[normalizedKey] = normalizedValue;
    }

    return sanitized;
  }

  return undefined;
}

function sanitizeAnalyticsParams(params: AnalyticsParams): AnalyticsParams {
  const sanitized: AnalyticsParams = {};

  for (const [key, value] of Object.entries(params)) {
    const normalizedKey = normalizeAnalyticsParamKey(key);
    const keyValidation = validateAnalyticsParamKey(normalizedKey);
    if (!keyValidation.valid) {
      warnInDev("Dropping analytics param with invalid key.", key, keyValidation.reason);
      continue;
    }

    const normalizedValue = sanitizeAnalyticsParamValue(value);
    if (normalizedValue === undefined) {
      continue;
    }

    sanitized[normalizedKey] = normalizedValue;
  }

  return sanitized;
}

function consumePendingServerAnalyticsEvents(): ServerQueuedAnalyticsEvent[] {
  if (typeof window === "undefined") {
    return [];
  }

  const pendingEvents = window.gcPendingAnalyticsEvents;
  window.gcPendingAnalyticsEvents = [];

  if (!pendingEvents || pendingEvents.length === 0) {
    return [];
  }

  return pendingEvents.filter((event): event is ServerQueuedAnalyticsEvent => {
    return !!event && typeof event.name === "string";
  });
}

export function flushPendingServerAnalyticsEvents(): void {
  if (hasFlushedPendingServerEvents) {
    return;
  }
  hasFlushedPendingServerEvents = true;

  const pendingEvents = consumePendingServerAnalyticsEvents();
  pendingEvents.forEach((event) => {
    trackAnalyticsEvent(event.name, event.properties ?? {});
  });
}

export function trackAnalyticsEvent(eventName: string, params: AnalyticsParams = {}): void {
  const eventValidation = validateAnalyticsEventName(eventName);
  if (!eventValidation.valid) {
    warnInDev("Skipping analytics event with invalid name.", eventName, eventValidation.reason);
    return;
  }

  const commonParams = readCommonAnalyticsParams();
  const sanitizedParams = sanitizeAnalyticsParams({
    ...commonParams,
    ...params,
  });
  void logFirebaseAnalyticsEvent(eventName, sanitizedParams);
}

export function trackKnownAnalyticsEvent(
  eventName: WebNextKnownEventName,
  params: AnalyticsParams = {},
): void {
  trackAnalyticsEvent(eventName, params);
}

export function trackAnalyticsPageView(pathnameAndSearch?: string): void {
  if (typeof window === "undefined") {
    return;
  }

  const pagePath =
    pathnameAndSearch ?? `${window.location.pathname}${window.location.search}`;

  trackAnalyticsEvent("page_view", {
    page_path: pagePath,
    page_location: window.location.href,
    page_title: document.title,
  });
}

export function setAnalyticsUserId(
  userId: string | number | null | undefined,
): void {
  const normalized = userId === null || userId === undefined ? null : String(userId);
  void setFirebaseAnalyticsUserId(normalized);
}
